import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  GoogleMap,
  TrafficLayer,
  useJsApiLoader,
} from "@react-google-maps/api";
import ReactMapGL from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { db } from "../../firebase";
import { auth } from "../../firebase";
import { Timestamp } from "firebase/firestore";
// Data goes through the board's data seam: Firestore, the Realtime Database and
// the hydrant tiles on a real board, the sample agency in the Studio's preview.
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  onSnapshot,
  getDoc,
  isDemoData,
  loadHydrants as loadHydrantsForArea,
  watchRealtime,
} from "../../board/boardData";
import { DEMO_MAP_TOKEN, demoMapStyle } from "../../board/demo/demoBasemap";
import FloatingMapCard from "../FloatingMapCard/floatingMapCard";
import alarmSound from "./fire_emergency.mp3";
import { playAlarmTone } from "../../utils/alarmTone";
import {
  MapAnimations,
  MapboxIncidentLayers,
  GoogleIncidentLayers,
  NoMappingScreen,
  LoadingMapScreen,
  nearestHydrants,
  extractCallerCoordinates as parseCallerCoordinates,
  MAPBOX_INCIDENT_STYLE,
  MAPBOX_INCIDENT_ZOOM,
  GOOGLE_INCIDENT_ZOOM,
  GOOGLE_INCIDENT_OPTIONS,
  ALARM_CYCLE_MS,
} from "./incidentMapParts";
import ScrollingText from "../ScrollingText/ScrollingText";
const showStyle = {
  display: "block",
};
const hideStyle = {
  display: "none",
};

// `agency` is passed when a Studio board shows the legacy layout (per-board
// accounts have no users/{uid} profile); the legacy board resolves it from the
// signed-in user's profile as it always has.
const FullScreenMap = ({ alarms, setAlarms, selectedTab, setSelectedTab, agency: boardAgency }) => {
  const [currentLocation, setCurrentLocation] = useState(null);
  const [agency, setAgency] = useState(null);
  const [agencyLocation, setAgencyLocation] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [floatingMapCardText, setFloatingMapCardText] = useState(null);
  const [posts, setPosts] = useState([]);
  const [activePostIndex, setActivePostIndex] = useState(0);
  const [postText, setPostText] = useState("");
  const [currentMarkerIndex, setCurrentMarkerIndex] = useState(0);
  const [delayedAudio, setDelayedAudio] = useState(false);
  const [hasCallins, setHasCallins] = useState(false);
  const [agencyLogoUrl, setAgencyLogoUrl] = useState("");
  const [vehicles, setVehicles] = useState([]);
  const [hydrants, setHydrants] = useState([]);
  const [secondaryAgencies, setSecondaryAgencies] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [callerLocation, setCallerLocation] = useState(null);
  const [useGoogleMaps, setUseGoogleMaps] = useState(false); // Default to Mapbox
  const [mapboxError, setMapboxError] = useState(false); // Track Mapbox errors for failover
  const mapRef = useRef(null);
  const currentIndexRef = useRef(-1); // Use ref to persist index across re-renders

  const hasInvalidAddress = alarms.length > 0 && (!alarms[currentMarkerIndex]?.Latitude || alarms[currentMarkerIndex]?.Latitude === "" || !alarms[currentMarkerIndex]?.Longitude || alarms[currentMarkerIndex]?.Longitude === "");

  // Always load Google Maps API (only actually loaded if useGoogleMaps becomes true)
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY, 
    libraries: [],
    id: 'google-map-script',
  });
  
  // Log when Google Maps API is loaded
  useEffect(() => {
    if (useGoogleMaps && isLoaded) {
      console.log('🗺️ Google Maps API LOADED');
    }
    if (useGoogleMaps && loadError) {
      console.error('❌ Google Maps API LOAD ERROR:', loadError);
    }
  }, [isLoaded, loadError, useGoogleMaps]);
  
  useEffect(() => {
    setDelayedAudio(true);

    const refreshInterval = setInterval(() => {
      setTimeout(() => {
        setDelayedAudio(false);
      }, 15000);

      window.location.reload();
    }, 60 * 60 * 1000); 

    return () => {
      clearInterval(refreshInterval);
    };
  }, []);

  useEffect(() => {
    if (agency) {
      const fetchAgencySettings = async () => {
        const settingsCollectionRef = collection(db, agency + " Settings");
  
        onSnapshot(settingsCollectionRef, (querySnapshot) => {
          const data = querySnapshot.docs.map((doc) => doc.data());
          if (data && data.length > 0) {
            setAgencyLogoUrl(data[0].logo);
          } else {
            console.error("No agency settings found");
          }
        });
      };
  
      fetchAgencySettings();
    }
  }, [agency]);

  useEffect(() => {
    if (agency) {
      const infoDocRef = doc(db, "Agencies", agency, "Settings", "Info");

getDoc(infoDocRef)
  .then((docSnapshot) => {
    if (docSnapshot.exists()) {
      const infoData = docSnapshot.data();
      setAgencyLocation({
        lat: parseFloat(infoData.lat),
        lng: parseFloat(infoData.long),
      });
      console.log('Fetched agencyLocation from database:', {
        lat: parseFloat(infoData.lat),
        lng: parseFloat(infoData.long),
      });
      console.log("Agency location fetched:", {
        lat: parseFloat(infoData.lat),
        lng: parseFloat(infoData.long),
      });
    } else {
      console.error("Info document does not exist");
    }
  })
  .catch((error) => {
    console.error("Error fetching agency info:", error);
  });

     
    }
  }, [agency]);


  
  useEffect(() => {
    if (boardAgency) {
      setAgency(boardAgency);
      setSecondaryAgencies([]);
      return undefined;
    }
    const unsubscribeAuth = auth.onAuthStateChanged((currentUser) => {
      if (currentUser) {
        const userDocRef = doc(db, "users", currentUser.uid);
        const unsubscribe = onSnapshot(userDocRef, (doc) => {
          if (!doc.exists()) {
          } else {
            const dataObj = doc.data();

            setAgency(dataObj.agency);
            setSecondaryAgencies(Array.isArray(dataObj.secondaryagencys) ? dataObj.secondaryagencys : []);

            if (dataObj.disabled) {
              // Replace this with your navigation logic
            }
          }
        });

        return () => {
          unsubscribe();
        };
      }
    });
    return () => {
      unsubscribeAuth();
    };
  }, [boardAgency]);

  useEffect(() => {
    if (agency) {
      const currentTime = new Date();
      const threeHoursAgo = new Date(currentTime.getTime() - 3 * 60 * 60 * 1000);
      const oneHourAgo = new Date(currentTime.getTime() - 60 * 60 * 1000);
      
      const threeHoursAgoTimestamp = Timestamp.fromDate(threeHoursAgo);
      const oneHourAgoTimestamp = Timestamp.fromDate(oneHourAgo);

      // Create a Firestore query for alarms
      const alarmsCollection = collection(db, agency + " Alarms");
      const alarmsQuery = query(
        alarmsCollection,
        where("TimeStamp", ">=", threeHoursAgoTimestamp),
      );

      const unsubscribeAlarms = onSnapshot(alarmsQuery, (querySnapshot) => {
        querySnapshot.docChanges().forEach((change) => {
          const data = change.doc.data();
          if (change.type === "added" && !delayedAudio && !isDemoData()) {
            playAlarmTone(alarmSound);
          }
        });

        const filteredData = querySnapshot.docs
          .map(doc => {
            const data = doc.data();
            const docTimeStamp = data.TimeStamp.toDate();
            
            // Apply the same filtering logic as provided
            if (
              (data.hasOwnProperty('AlarmStatus') && data.AlarmStatus === 'true') || 
              (!data.hasOwnProperty('AlarmStatus') && docTimeStamp >= oneHourAgo) || 
              (data.callcard === '*DISPATCH*' && docTimeStamp >= oneHourAgo)
            ) {
              return { ...data, id: doc.id };
            }
            return null;
          })
          .filter(Boolean); // Remove null entries

        getMarkers(filteredData);
      });

      // Try nested structure first: Agencies/{agency}/Station Board
      const nestedDeptCollection = collection(db, "Agencies", agency, "Station Board");
      const nestedDeptQuery = query(nestedDeptCollection);
      const unsubscribeNestedDept = onSnapshot(nestedDeptQuery, (querySnapshot) => {
        const data = querySnapshot.docs.map((doc) => doc.data());
        console.log('📄 Firestore nested Station Board query returned:', data.length, 'documents');
        if (data && data.length > 0) {
          console.log('📄 Firestore Station Board document data:', data[0]);
          if (data[0].stationLat && data[0].stationLng) {
            setCurrentLocation({ lat: parseFloat(data[0].stationLat), lng: parseFloat(data[0].stationLng) });
          }
          // Check if googleMaps boolean exists, default to false (use Mapbox)
          if (data[0].hasOwnProperty('googleMaps')) {
            const shouldUseGoogle = data[0].googleMaps === true;
            // Only use Firestore setting if Mapbox hasn't errored
            if (!mapboxError) {
              setUseGoogleMaps(shouldUseGoogle);
            }
            console.log('🗺️ Map provider setting from Firestore - googleMaps field:', data[0].googleMaps, 'Type:', typeof data[0].googleMaps);
            console.log('🗺️ Setting useGoogleMaps to:', mapboxError ? true : shouldUseGoogle);
          } else {
            // If Mapbox has errored, use Google Maps regardless
            setUseGoogleMaps(mapboxError ? true : false);
            console.log('🗺️ No googleMaps setting found, defaulting to', mapboxError ? 'Google Maps (Mapbox failover)' : 'Mapbox');
          }
        }
      });
      
      // Fallback: Try flat structure: "{agency} Station Board"
      const flatDeptCollection = collection(db, agency + " Station Board");
      const flatDeptQuery = query(flatDeptCollection);
      const unsubscribeFlatDept = onSnapshot(flatDeptQuery, (querySnapshot) => {
        const data = querySnapshot.docs.map((doc) => doc.data());
        console.log('📄 Firestore flat Station Board query returned:', data.length, 'documents');
        // Only use flat data if nested didn't return anything
        if (data && data.length > 0) {
          if (data[0].stationLat && data[0].stationLng) {
            setCurrentLocation({ lat: parseFloat(data[0].stationLat), lng: parseFloat(data[0].stationLng) });
          }
        }
      });

      // Create a Firestore query for the last 24 hours

      const postsCollection = collection(db, agency);
      const postsQuery = query(
        postsCollection,
        where("privacy", "in", ["All", "Stationboard"])
    );
      const unsubscribePosts = onSnapshot(postsQuery, (querySnapshot) => {
        const data = querySnapshot.docs.map((doc) => doc.data());
        const postsWithSeconds = data.filter(
          (post) => post.postTime && post.postTime.seconds
        );

        postsWithSeconds.sort((a, b) => {
          return b.postTime.seconds - a.postTime.seconds;
        });
        let newPosts = postsWithSeconds.slice(0, 5);

        setPosts(newPosts);
      });

      return () => {
        unsubscribeAlarms();
        unsubscribeNestedDept();
        unsubscribeFlatDept();
        unsubscribePosts();
      };
    }
  }, [agency]);

  // Load units from agencies
  useEffect(() => {
    // Function to load units from a single agency
    const loadUnitsFromAgency = (agencyId) => {
      return watchRealtime(`/UnitLocation/${agencyId}`, (unitsData) => {
        
        if (unitsData) {
          // Calculate the timestamp for 24 hours ago
          const oneDayAgo = new Date();
          oneDayAgo.setHours(oneDayAgo.getHours() - 24);
          const oneDayAgoTimestamp = oneDayAgo.getTime();
          
          // Create unit array and filter by timestamp
          const unitArray = Object.keys(unitsData)
            .map(key => ({
              responderName: key,
              agency: agencyId, // Add agency ID to track which agency each unit belongs to
              ...unitsData[key],
            }))
            .filter(unit => {
              // Check if the unit has a valid timestamp
              const unitTimestamp = unit.timestamp || 0;
              
              // Keep only units updated within the last 24 hours
              const isRecent = unitTimestamp >= oneDayAgoTimestamp;
              
              return isRecent;
            });
          
          // Merge with existing units
          setVehicles(prevVehicles => {
            // Filter out any previous units from this agency to avoid duplicates
            const filteredPrevVehicles = prevVehicles.filter(v => v.agency !== agencyId);
            return [...filteredPrevVehicles, ...unitArray];
          });
        }
      });
    };
    
    // Track all database listeners to unsubscribe later
    const listeners = [];
    
    // Load units from the agency (simplified version for FullScreenMap)
    if (agency) {
      listeners.push(loadUnitsFromAgency(agency));
    }
    
    // Return cleanup function to unsubscribe from all listeners
    return () => {
      listeners.forEach(unsubscribe => {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      });
    };
  }, [agency]);

  const extractCallerCoordinates = useCallback((eventComments) => {
    const coords = parseCallerCoordinates(eventComments);
    if (coords) setCallerLocation(coords);
  }, []);

  // Debug: Monitor callerLocation state changes
  useEffect(() => {
    console.log('CallerLocation state updated:', callerLocation);
  }, [callerLocation]);

  async function getMarkers(data) {
    const updatedData = await Promise.all(
      data.map(async (alarm) => {
        // Parse latitude and longitude from Firestore data
        let latitude = parseFloat(alarm.Latitude);
        let longitude = parseFloat(alarm.Longitude);
  
        if (isNaN(latitude) || isNaN(longitude)) {
          // Coordinates are missing or invalid - serverside geocoding should have handled this
          console.log(`Missing coordinates for alarm ID ${alarm.id}, address: ${alarm.Address}`);
        } else {
          // Coordinates are valid, use them
          console.log(`Using coordinates from Firestore for alarm ID ${alarm.id}: ${latitude}, ${longitude}`);
        }

      // Update the alarm object with the latitude and longitude
      alarm.lat = latitude;
      alarm.lng = longitude;

        const callinsRef = collection(db, `${agency} Alarms/${alarm.id}/Callins`);
        const callinsSnapshot = await getDocs(callinsRef);
        alarm.Callins = callinsSnapshot.docs.map((callinDoc) => {
          const callinData = callinDoc.data();
          return {
            ...callinData,
            qualifications: callinData.qualifications || "",
          };
        });

        onSnapshot(callinsRef, (snapshot) => {
          const updatedCallins = snapshot.docs.map((callinDoc) => {
            const callinData = callinDoc.data();
            return {
              ...callinData,
              qualifications: callinData.qualifications || "",
            };
          });

          alarm.Callins = updatedCallins;
          setAlarms((prevAlarms) => {
            const updatedAlarms = prevAlarms.map((prevAlarm) =>
              prevAlarm.id === alarm.id ? { ...prevAlarm, Callins: updatedCallins } : prevAlarm
            );
            return updatedAlarms;
          });
        });

        return alarm;
      })
    );
    setAlarms(updatedData);
  }
  useEffect(() => {
    const timer = setInterval(async () => {
      if (alarms.length > 0) {
        await checkAlarms();
  
        // Calculate the index of the next active marker
        currentIndexRef.current = (currentIndexRef.current + 1) % alarms.length;
        const currentIndex = currentIndexRef.current;
  
        const currentAlarm = alarms[currentIndex];
        console.log(`🔄 Cycling to alarm ${currentIndex + 1} of ${alarms.length}:`, currentAlarm?.id);
  
        // Set the active marker's position as the new current location
        setCurrentLocation(currentAlarm);
  
        // Update the floating map card text with the corresponding alarm
        setCurrentMarkerIndex(currentIndex);
        setFloatingMapCardText(currentAlarm);
        
        // Extract caller coordinates from EventComments if available
        console.log('Current alarm EventComments:', currentAlarm?.EventComments);
        if (currentAlarm?.EventComments) {
          console.log('Calling extractCallerCoordinates for current alarm');
          extractCallerCoordinates(currentAlarm.EventComments);
        } else {
          console.log('No EventComments found in current alarm');
        }
      } else {
        setFloatingMapCardText(null);
        setCurrentMarkerIndex(0);
        currentIndexRef.current = -1; // Reset index when no alarms
      }
  
      if (alarms.length === 0) {
        // Geocoding is now handled serverside
        setSelectedTab("dashboard");
      }
    }, ALARM_CYCLE_MS);
  
    return () => {
      clearInterval(timer);
    };
  }, [alarms, hasInvalidAddress, agencyLocation]);
  

  useEffect(() => {
    getPosts();
    const timer = setInterval(async () => {
      getPosts();
    }, 60000);

    return () => {
      clearInterval(timer);
    };
  }, [posts]);

  async function getPosts() {
    let currentIndex = -1;
    if (posts.length > 0) {
      currentIndex = (currentIndex + 1) % posts.length;
      setActivePostIndex(currentIndex);

      try {
        const documentSnapshot = await getDoc(
          doc(db, "users", posts[currentIndex].userId)
        );

        if (documentSnapshot.exists() && documentSnapshot.data()?.name) {
          setPostText(
            posts[currentIndex].post + " -- " + documentSnapshot.data().name
          );
        } else {
          setPostText(posts[currentIndex].post);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        setPostText(posts[currentIndex].post);
      }
    } else {
      setPostText("");
      setActivePostIndex(-1);
    }
  }

  function checkAlarms() {
    return new Promise((resolve, reject) => {
      var newAlarms = alarms;
      for (let i = 0; i < newAlarms.length; i++) {
        let marker = newAlarms[i];
        let time = marker.TimeStamp;
        let date = time.toDate();
        let now = new Date();

        const timeDifference = Math.abs(date - now);

        // Calculate the number of milliseconds in an hour
        const millisecondsInThreeHours = 3 * 60 * 60 * 1000;
        if (timeDifference > millisecondsInThreeHours) {
          newAlarms.splice(i, 1);
        }
      }
      setAlarms(newAlarms);
      resolve();
    });
  }

  // Memoize containerStyle to prevent unnecessary re-renders
  const containerStyle = useMemo(() => ({
    width: "100vw",
    height: "100vh",
    display: selectedTab === "map" ? "block" : "none",
  }), [selectedTab]);
  
  // Memoize mapOptions to prevent map reloads
  const mapOptions = useMemo(() => GOOGLE_INCIDENT_OPTIONS, []);

  // Memoize center to prevent unnecessary map panning
  const mapCenter = useMemo(() => {
    console.log('📍 Map center memoized:', currentLocation);
    return currentLocation;
  }, [currentLocation?.lat, currentLocation?.lng]);

  // Center Mapbox map on current alarm when it changes
  useEffect(() => {
    if (!useGoogleMaps && mapRef.current && alarms.length > 0 && currentMarkerIndex < alarms.length) {
      const currentAlarm = alarms[currentMarkerIndex];
      if (currentAlarm?.lat && currentAlarm?.lng) {
        mapRef.current.flyTo({
          center: [currentAlarm.lng, currentAlarm.lat],
          zoom: MAPBOX_INCIDENT_ZOOM,
          duration: 1000,
          essential: true
        });
        console.log('🎯 Mapbox centering on alarm:', currentAlarm);
      }
    }
  }, [currentMarkerIndex, alarms, useGoogleMaps]);

  // ---- Fire hydrants (POI-tile pipeline, mirrors LadderApp) ----
  // Center hydrant loading on the alarm the map is currently showing.
  const hydrantCenter = useMemo(() => {
    const a = alarms[currentMarkerIndex];
    if (a && Number.isFinite(a.lat) && Number.isFinite(a.lng)) return { lat: a.lat, lng: a.lng };
    if (currentLocation && Number.isFinite(currentLocation.lat) && Number.isFinite(currentLocation.lng)) {
      return { lat: currentLocation.lat, lng: currentLocation.lng };
    }
    return null;
  }, [alarms, currentMarkerIndex, currentLocation]);

  // Fetch hydrants for a ~0.1° box around the current alarm (matches LadderApp's
  // HydrantList). Metadata + payload caches make overlapping re-fetches nearly
  // free; errors are swallowed so the map never breaks.
  useEffect(() => {
    if (!agency || !hydrantCenter) return;
    let active = true;
    const HALF = 0.05; // ~0.1° box
    const area = {
      bounds: {
        north: hydrantCenter.lat + HALF,
        south: hydrantCenter.lat - HALF,
        east: hydrantCenter.lng + HALF,
        west: hydrantCenter.lng - HALF,
      },
    };
    (async () => {
      try {
        const { pois } = await loadHydrantsForArea(area, {
          agencies: [agency, ...secondaryAgencies],
        });
        if (active) setHydrants(pois.map((p) => p.poi));
      } catch (err) {
        if (active) console.error("Hydrant load failed", err);
      }
    })();
    return () => {
      active = false;
    };
  }, [agency, secondaryAgencies, hydrantCenter]);

  // Cap markers (each engine creates one DOM/overlay node per marker):
  // nearest-to-center first so the most relevant hydrants always render.
  const hydrantMarkers = useMemo(() => nearestHydrants(hydrants, hydrantCenter), [hydrants, hydrantCenter]);

  // Automatically switch to Google Maps if Mapbox encounters an error
  useEffect(() => {
    if (mapboxError && !useGoogleMaps) {
      console.warn('⚠️ MAPBOX FAILOVER: Switching to Google Maps due to Mapbox error');
      setUseGoogleMaps(true);
    }
  }, [mapboxError, useGoogleMaps]);

  // Only check loading status for Google Maps if we're using it
  if (useGoogleMaps && !isLoaded) {
    return <div>Loading Map...</div>;
  }

  if (useGoogleMaps && loadError) {
    return <div>Error loading maps</div>;
  }

  // Log when component renders
  console.log('🔄 FullScreenMap component render');
  console.log('🗺️ useGoogleMaps state:', useGoogleMaps, 'Type:', typeof useGoogleMaps);
  console.log('🗺️ mapboxError state:', mapboxError);
  console.log('🗺️ Will render:', useGoogleMaps ? 'Google Maps' : 'Mapbox');
  
  return (
    <div style={{ position: "relative" }}>
      {/* Global animations for both map types */}
      <MapAnimations />
      { !hasInvalidAddress && alarms.length > 0 && currentLocation ? (
        useGoogleMaps ? (
          // GOOGLE MAPS
          console.log('🗺️ GoogleMap component rendering with center:', mapCenter) || <GoogleMap
          mapContainerStyle={containerStyle}
          center={mapCenter}
          zoom={GOOGLE_INCIDENT_ZOOM}
          options={mapOptions}
          onLoad={(map) => console.log('🎯 GoogleMap INITIALIZED/LOADED - This means a NEW map API call!')}
          onUnmount={() => console.log('🔴 GoogleMap UNMOUNTED')}
        >
          <TrafficLayer autoRefresh />
          <GoogleIncidentLayers
            alarms={alarms}
            hydrants={hydrantMarkers}
            vehicles={vehicles}
            callerLocation={callerLocation}
            selectedVehicle={selectedVehicle}
            onSelectVehicle={setSelectedVehicle}
          />
        </GoogleMap>
      ) : (
          // MAPBOX (Default)
          <ReactMapGL
            ref={mapRef}
            mapboxAccessToken={isDemoData() ? DEMO_MAP_TOKEN : process.env.REACT_APP_MAPBOX_TOKEN}
            initialViewState={{
              longitude: alarms.length > 0 && alarms[currentMarkerIndex]?.lng ? alarms[currentMarkerIndex].lng : currentLocation.lng,
              latitude: alarms.length > 0 && alarms[currentMarkerIndex]?.lat ? alarms[currentMarkerIndex].lat : currentLocation.lat,
              zoom: MAPBOX_INCIDENT_ZOOM
            }}
            style={{ width: '100vw', height: '100vh', display: selectedTab === "map" ? "block" : "none" }}
            mapStyle={isDemoData() ? demoMapStyle() : MAPBOX_INCIDENT_STYLE}
            onLoad={(evt) => {
              console.log('🎯 Mapbox Map INITIALIZED/LOADED');
              // Center on current alarm after map loads
              if (alarms.length > 0 && currentMarkerIndex < alarms.length) {
                const currentAlarm = alarms[currentMarkerIndex];
                if (currentAlarm?.lat && currentAlarm?.lng) {
                  evt.target.flyTo({
                    center: [currentAlarm.lng, currentAlarm.lat],
                    zoom: MAPBOX_INCIDENT_ZOOM,
                    duration: 1000
                  });
                }
              }
            }}
            onError={(error) => {
              console.error('❌ MAPBOX ERROR:', error);
              console.warn('⚠️ Initiating failover to Google Maps...');
              setMapboxError(true);
            }}
          >
            <MapboxIncidentLayers
              alarms={alarms}
              hydrants={hydrantMarkers}
              vehicles={vehicles}
              callerLocation={callerLocation}
              selectedVehicle={selectedVehicle}
              onSelectVehicle={setSelectedVehicle}
            />
          </ReactMapGL>
        )
      ) : hasInvalidAddress ? (
        // Render the background with agency logo and message
        <NoMappingScreen logoUrl={agencyLogoUrl} style={{ display: selectedTab === "map" ? "flex" : "none" }} />
      ) : (
        <LoadingMapScreen style={{ display: selectedTab === "map" ? "flex" : "none" }} />
      )}
      <FloatingMapCard
        text={floatingMapCardText}
        callId={floatingMapCardText?.id}
        agency={agency}
        onCallinsChange={setHasCallins}
        isFullscreenMapVisible={selectedTab === "map"}
      />
    </div>
  );
  
  
};

export default FullScreenMap;